import {TJobStatusResponse} from "./TInterviewThreadApi";
import {isProcessingJobStatus, TInterviewProcessingJob} from "./TInterviewThread";

export type TProcessingJobMonitorCallbacks = {
    onStatus: (
        job: TInterviewProcessingJob,
        failedSince: number|null,
    ) => Promise<void>|void,
    onFailedTimeout: (jobId: number) => Promise<void>|void,
    onError: (error: unknown, jobId: number) => Promise<void>|void,
};

export default class ProcessingJobMonitor {

    public static readonly INTERVAL_MS = 800;
    public static readonly FAILED_TIMEOUT_MS = 60000;

    private timeoutId: ReturnType<typeof setTimeout>|null = null;
    private requestController: AbortController|null = null;
    private generation = 0;
    private failedSince: number|null = null;

    constructor(
        private readonly loadStatus: (
            jobId: number,
            signal: AbortSignal,
        ) => Promise<TJobStatusResponse>,
        private readonly intervalMs = ProcessingJobMonitor.INTERVAL_MS,
        private readonly failedTimeoutMs = ProcessingJobMonitor.FAILED_TIMEOUT_MS,
        private readonly now: () => number = () => performance.now(),
    ) {
    }

    public start(
        jobId: number,
        callbacks: TProcessingJobMonitorCallbacks,
        immediate = false,
    ): void {
        this.stop();
        const generation = this.generation;
        this.failedSince = null;
        this.schedule(jobId, callbacks, generation, immediate ? 0 : this.intervalMs);
    }

    public stop(): void {
        this.generation++;
        this.failedSince = null;
        this.clearScheduledRequest();
    }

    private schedule(
        jobId: number,
        callbacks: TProcessingJobMonitorCallbacks,
        generation: number,
        delay: number,
    ): void {
        this.timeoutId = setTimeout(
            () => void this.poll(jobId, callbacks, generation),
            delay,
        );
    }

    private async poll(
        jobId: number,
        callbacks: TProcessingJobMonitorCallbacks,
        generation: number,
    ): Promise<void> {
        if (generation !== this.generation) {
            return;
        }

        this.timeoutId = null;
        const requestController = new AbortController();
        this.requestController = requestController;

        try {
            const response = await this.loadStatus(jobId, requestController.signal);
            if (generation !== this.generation || requestController.signal.aborted) {
                return;
            }
            this.requestController = null;

            const status = response.job.status.toLowerCase();
            if (status === "failed") {
                this.failedSince ??= this.now();
                if (this.now() - this.failedSince >= this.failedTimeoutMs) {
                    this.clearScheduledRequest();
                    await callbacks.onFailedTimeout(jobId);
                    return;
                }
            } else if (isProcessingJobStatus(status)
                && status !== "done"
                && status !== "dead"
            ) {
                this.failedSince = null;
            }

            await callbacks.onStatus(response.job, this.failedSince);
            if (generation !== this.generation) {
                return;
            }

            if (status === "pending"
                || status === "sent"
                || status === "processing"
                || status === "failed"
            ) {
                this.schedule(jobId, callbacks, generation, this.intervalMs);
            }
        } catch (error) {
            if (generation !== this.generation || requestController.signal.aborted) {
                return;
            }
            this.requestController = null;
            await callbacks.onError(error, jobId);
        }
    }

    private clearScheduledRequest(): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (this.requestController !== null) {
            this.requestController.abort();
            this.requestController = null;
        }
    }

}
